import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import axios from 'axios';
import { API_URL } from '../../../common/Constants';
import Cookies from '../../../common/Cookies';
import { useReseller } from '../../../contexts/ResellerContext';
import StripePortalButton from './StripePortalButton';

const BillingInfoSection = ({ accountData, handleChange, onSave }) => {
  const { reseller } = useReseller();
  const productName = reseller?.productName || 'Chattitude';
  const showStripe = reseller == null || reseller.useStripeForBilling !== false;
  const isPaidPlan =
    accountData.currentPlan && ['S', 'P', 'U'].includes(accountData.currentPlan);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSaveBilling = async () => {
    setIsSaving(true);

    try {
      const billingData = {
        useSameContactInfo: accountData.useSameContactInfo,
        billingFirstName: accountData.billingFirstName,
        billingLastName: accountData.billingLastName,
        billingCompany: accountData.billingCompany,
        billingAddress: accountData.billingAddress,
        billingAddress2: accountData.billingAddress2,
        billingCity: accountData.billingCity,
        billingState: accountData.billingState,
        billingZip: accountData.billingZip,
        billingPhone: accountData.billingPhone
      };

      const response = await axios.post(`${API_URL}/account/billing/save`, billingData, {
        headers: {
          'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
        }
      });

      if (response.data.success) {
        showSnackbar('✅ Billing information saved successfully!', 'success');
        // Show testing warning if present
        if (response.data.testingWarning) {
          setTimeout(() => {
            showSnackbar(response.data.testingWarning, 'warning');
          }, 2000);
        }
        if (onSave) onSave();
      } else {
        showSnackbar('❌ Error: ' + response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error saving billing info:', error);
      showSnackbar('❌ Error saving billing information', 'error');
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>Billing Information</Typography>

        {showStripe && isPaidPlan && (
          <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Payment methods and invoice billing details on file with Stripe are managed in Stripe’s customer portal.
              Use the button below to update your card or Stripe billing profile. The address fields below are stored in your {productName} account for our records.
            </Typography>
            <StripePortalButton
              label="Open Stripe billing portal"
              size="small"
              onError={(msg) => showSnackbar(msg || 'Failed to open Stripe billing portal', 'error')}
            />
          </Alert>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={accountData.useSameContactInfo}
              onChange={(e) => handleChange('billing', 'useSameContactInfo', e.target.checked)}
              color="primary"
            />
          }
          label="Use contact information for billing"
          sx={{ mb: 2, textAlign: 'left', display: 'flex', justifyContent: 'flex-start' }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: { xs: '100%', sm: 400, md: 500 }, ml: 0 }}>
          <TextField
            fullWidth
            label="First Name"
            variant="outlined"
            value={accountData.useSameContactInfo ? accountData.firstName : accountData.billingFirstName}
            onChange={(e) => handleChange('billing', 'billingFirstName', e.target.value)}
            disabled={accountData.useSameContactInfo}
          />

          <TextField
            fullWidth
            label="Last Name"
            variant="outlined"
            value={accountData.useSameContactInfo ? accountData.lastName : accountData.billingLastName}
            onChange={(e) => handleChange('billing', 'billingLastName', e.target.value)}
            disabled={accountData.useSameContactInfo}
          />

          <TextField
            fullWidth
            label="Company"
            variant="outlined"
            value={accountData.useSameContactInfo ? accountData.company : accountData.billingCompany}
            onChange={(e) => handleChange('billing', 'billingCompany', e.target.value)}
            disabled={accountData.useSameContactInfo}
          />

          <TextField
            fullWidth
            label="Address"
            variant="outlined"
            value={accountData.billingAddress}
            onChange={(e) => handleChange('billing', 'billingAddress', e.target.value)}
          />

          <TextField
            fullWidth
            label="Address Line 2 (Optional)"
            variant="outlined"
            value={accountData.billingAddress2}
            onChange={(e) => handleChange('billing', 'billingAddress2', e.target.value)}
          />

          <TextField
            fullWidth
            label="City"
            variant="outlined"
            value={accountData.billingCity}
            onChange={(e) => handleChange('billing', 'billingCity', e.target.value)}
          />

          <TextField
            fullWidth
            label="State"
            variant="outlined"
            value={accountData.billingState}
            onChange={(e) => handleChange('billing', 'billingState', e.target.value)}
          />

          <TextField
            fullWidth
            label="Zip Code"
            variant="outlined"
            value={accountData.billingZip}
            onChange={(e) => handleChange('billing', 'billingZip', e.target.value)}
          />

          <TextField
            fullWidth
            label="Phone Number"
            variant="outlined"
            value={accountData.useSameContactInfo ? accountData.phone : accountData.billingPhone}
            onChange={(e) => handleChange('billing', 'billingPhone', e.target.value)}
            disabled={accountData.useSameContactInfo}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveBilling}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ mt: 2 }}
          >
            {isSaving ? 'Saving...' : 'Save Billing Info'}
          </Button>
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BillingInfoSection;